import React, { useState, useEffect } from "react";
import { Api } from "../../network/api";
import PlanModal from "./PlanModal";
import Button from "../../component/shared/Button";
import { AlertCircleIcon, AddIcon, DeleteIcon } from "../../assets/icons";

const PlansManagement = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [modalMode, setModalMode] = useState("create");
  const [deleteLoading, setDeleteLoading] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await Api.getPlans();
      setPlans(response.data?.plans || []);
    } catch (err) {
      console.error("Error fetching plans:", err);
      setError(err.response?.data?.detail || "Failed to fetch plans");
    } finally {
      setLoading(false);
    }
  };

  const handleModalSuccess = () => {
    fetchPlans();
    setIsModalOpen(false);
    setSelectedPlan(null);
    setModalMode("create");
  };

  const handleCreateClick = () => {
    setModalMode("create");
    setSelectedPlan(null);
    setIsModalOpen(true);
  };

  const handleUpdateClick = (plan) => {
    setModalMode("edit");
    setSelectedPlan(plan);
    setIsModalOpen(true);
  };

  const handleDeleteClick = async (plan) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the plan "${plan.name}"? This will also delete all associated prices. This action cannot be undone.`
      )
    ) {
      return;
    }

    setDeleteLoading(plan.id);
    setError(null);
    setSuccessMessage(null);
    try {
      await Api.deletePlan(plan.id);
      setSuccessMessage(`Plan "${plan.name}" deleted successfully`);
      setTimeout(() => setSuccessMessage(null), 5000);
      fetchPlans();
    } catch (err) {
      console.error("Error deleting plan:", err);
      setError(err.response?.data?.detail || "Failed to delete plan");
    } finally {
      setDeleteLoading(null);
    }
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase() || "USD",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Available Plans</h1>
          <p className="text-lg text-gray-600">
            Manage subscription plans for users
          </p>
        </div>

        <div className="flex justify-end mb-8">
          <Button onClick={handleCreateClick}>
            <div className="flex items-center space-x-2">
              <AddIcon width="16" height="16" />
              <span>Create Plan</span>
            </div>
          </Button>
        </div>

        {error && (
          <div className="mb-6 animate-fadeIn">
            <div className="bg-white border border-red-200 rounded-xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-red-50 to-red-100 px-6 py-4 border-b border-red-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center">
                        <AlertCircleIcon width="24" height="24" className="text-white" />
                      </div>
                    </div>
                    <div className="px-6 py-4">
                      <p className="text-gray-800 leading-relaxed">{error}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setError(null)}
                    className="text-red-400 hover:text-red-600 transition-colors p-1 rounded-lg hover:bg-red-50"
                    aria-label="Dismiss error"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

            </div>
          </div>
        )}

        {successMessage && (
          <div className="mb-6 animate-fadeIn">
            <div className="bg-white border border-green-200 rounded-xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-green-50 to-green-100 px-6 py-4 border-b border-green-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                        <svg
                          className="w-6 h-6 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-green-900">Success</h3>
                      <p className="text-sm text-green-700 mt-0.5">Operation completed successfully</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSuccessMessage(null)}
                    className="text-green-400 hover:text-green-600 transition-colors p-1 rounded-lg hover:bg-green-50"
                    aria-label="Dismiss success message"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="px-6 py-4">
                <p className="text-gray-800 leading-relaxed">{successMessage}</p>
              </div>
            </div>
          </div>
        )}

        {plans.length === 0 ? (
          <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-12 text-center">
            <div className="max-w-md mx-auto">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No plans found</h3>
              <p className="mt-2 text-sm text-gray-500">
                Get started by creating your first subscription plan.
              </p>
              <div className="mt-6">
                <Button onClick={handleCreateClick}>
                  <div className="flex items-center space-x-2">
                    <AddIcon width="16" height="16" />
                    <span>Create Plan</span>
                  </div>
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {plans.map((plan, index) => {
              const isPopular = plan.active && index === Math.floor(plans.length / 2);
              const activePrice = plan.prices?.find((p) => p.active) || plan.prices?.[0];

              return (
                <div
                  key={plan.id}
                  className={`bg-white rounded-xl shadow-sm border-2 overflow-hidden transition-all duration-300 hover:shadow-lg flex flex-col h-full ${isPopular
                      ? "border-red-600 relative"
                      : "border-gray-200"
                    }`}
                >
                  <div className="flex-1 flex flex-col">
                    <div className="px-6 pt-8 pb-6 text-left">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                      {plan.description && (
                        <p className="text-sm text-gray-600 leading-relaxed mb-6">
                          {plan.description}
                        </p>
                      )}

                      {activePrice && (
                        <div className="mb-6">
                          <div className="flex items-baseline">
                            <span className="text-4xl font-bold text-gray-900">
                              {formatCurrency(activePrice.amount, activePrice.currency)}
                            </span>
                            {/* <span className="text-gray-600 ml-2 text-lg">
                              / {activePrice.interval || "N/A"} {activePrice.interval ? "days" : ""}
                            </span> */}
                          </div>
                        </div>
                      )}
                      <div className="mb-6 p-3 bg-red-50 rounded-lg border border-red-100">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-700 font-medium">Allowed Essays</span>
                          <span className="text-lg font-bold text-red-700">
                            {plan.allowed_essays || 0}
                          </span>
                        </div>
                      </div>
                    </div>

                    {plan.features && plan.features.length > 0 && (
                      <div className="px-6 pb-6 flex-1">
                        <ul className="space-y-3">
                          {plan.features.map((feature, featureIndex) => (
                            <li
                              key={featureIndex}
                              className="flex items-start text-left space-x-3"
                            >
                              {feature.included ? (
                                <svg
                                  className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                              <div className="flex-1">
                                <span
                                  className={`text-sm ${feature.included
                                      ? "text-gray-900"
                                      : "text-gray-400 line-through"
                                    }`}
                                >
                                  {feature.name}
                                </span>
                                {!feature.included && (
                                  <span className="text-xs text-gray-400 ml-2">
                                    {/* (Hidden from users) */}
                                  </span>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Footer Buttons - Fixed at bottom */}
                  <div className="px-6 pb-6 pt-4 border-t border-gray-100 mt-auto">
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleUpdateClick(plan)}
                        className="flex-1 py-3 px-4 rounded-lg font-medium transition-colors bg-red-600 text-white hover:bg-red-700 border-2 border-red-600"
                      >
                        Update
                      </button>
                      <button
                        onClick={() => handleDeleteClick(plan)}
                        disabled={deleteLoading === plan.id}
                        className="flex-1 py-3 px-4 rounded-lg font-medium transition-colors bg-white text-red-600 border-2 border-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                      >
                        {deleteLoading === plan.id ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                            <span>Deleting...</span>
                          </>
                        ) : (
                          <>
                            <DeleteIcon width="16" height="16" />
                            <span>Delete</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Unified Plan Modal */}
        <PlanModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedPlan(null);
            setModalMode("create");
          }}
          onSuccess={handleModalSuccess}
          plan={selectedPlan}
          mode={modalMode}
        />
      </div>
    </div>
  );
};

export default PlansManagement;

